"use client";
import { useEffect, useRef, useState } from "react";
import ChatBubble from "./ChatBubble";
import styles from "./index.module.css";
import { replyIcon } from "../../assets";

const isTouchDevice = typeof window !== "undefined" && "ontouchstart" in window;
const SWIPE_THRESHOLD = isTouchDevice ? 120 : 80; // bigger swipe needed on touch
const LOCK_POINT = SWIPE_THRESHOLD / 2;
const Y_TOLERANCE = isTouchDevice ? 35 : 10; // more wobble allowed on touch

const ChatMessage = ({
  message,
  currentUserId,
  highlightedMessageId,
  scrollToMessage,
  setHighlightedMessageId,
  handleOpenMedia,
  handleDelete,
  handleEdit,
  handleCopy,
  handleReply,
  handleRetry,
  chatAreaRef,
  showSenderName = false,
}) => {
  const [hovered, setHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [hasSwiped, setHasSwiped] = useState(false);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const [swipeDistance, setSwipeDistance] = useState(0);
  const longPressTimer = useRef(null);
  const hasLockedRef = useRef(false);

  const bubbleRef = useRef(null);
  const dropdownRef = useRef(null);
  const isTouchDevice =
    typeof window !== "undefined" && "ontouchstart" in window;

  // outside tap handler
  useEffect(() => {
    if (!showDropdown) return;

    const handleOutsideTouch = (e) => {
      if (bubbleRef.current && !bubbleRef.current.contains(e.target)) {
        hovered && setHovered(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        closeMenu();
      }
    };

    document.addEventListener("touchstart", handleOutsideTouch);
    document.addEventListener("mousedown", handleOutsideTouch);
    return () => {
      document.removeEventListener("touchstart", handleOutsideTouch);
      document.removeEventListener("mousedown", handleOutsideTouch);
    };
  }, [showDropdown, hovered]);

  useEffect(() => {
    return () => {
      clearTimeout(longPressTimer.current);
    };
  }, []);

  useEffect(() => {
    // 🔹 Haptic at half lock
    if (swipeDistance >= LOCK_POINT && !hasLockedRef.current) {
      hasLockedRef.current = true;
      if (navigator.vibrate) {
        navigator.vibrate(10); // subtle feedback
      }
    }

    // 🔹 Haptic at full threshold
    if (
      swipeDistance >= SWIPE_THRESHOLD &&
      hasLockedRef.current !== "threshold"
    ) {
      hasLockedRef.current = "threshold";
      if (navigator.vibrate) {
        navigator.vibrate([15, 30, 15]); // slightly stronger pattern
      }
    }

    // Reset state when gesture ends
    if (swipeDistance === 0) {
      hasLockedRef.current = false;
    }
  }, [swipeDistance]);

  const handleStart = (clientX, clientY, id) => {
    if (highlightedMessageId && highlightedMessageId !== id) {
      closeMenu();
      return;
    }

    touchStartX.current = clientX;
    touchStartY.current = clientY;
    setIsHolding(true);
    setHasSwiped(false);

    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      if (!highlightedMessageId && !hasSwiped) {
        setShowDropdown(true);
        setHighlightedMessageId(id);
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }
      setIsHolding(false);
    }, 700);
  };

  const handleMove = (clientX, clientY) => {
    setHovered(false);
    const diffX = clientX - touchStartX.current;
    const diffY = Math.abs(clientY - touchStartY.current);

    // ✅ only trigger horizontal swipe if within tolerance
    if (diffX > 0 && diffX > diffY && diffY < Y_TOLERANCE) {
      if (!hasSwiped) {
        lockScroll(true);
      }
      setSwipeDistance(diffX);
      setHasSwiped(true);
      clearTimeout(longPressTimer.current); // cancel long press
    }
  };

  const handleEnd = () => {
    if (swipeDistance > SWIPE_THRESHOLD) {
      handleReply(message);
    }
    setSwipeDistance(0);
    clearTimeout(longPressTimer.current);
    setIsHolding(false);
    lockScroll(false);
  };

  const referencedId =
    message?.reference?.trackId || message?.reference?.chatId || null;

  const onClickReference = (e) => {
    e.stopPropagation();
    if (hasSwiped) return;
    if (!referencedId) return;
    if (typeof scrollToMessage === "function") {
      scrollToMessage(referencedId);
    }
  };

  const closeMenu = () => {
    setShowDropdown(false);
    setHighlightedMessageId(null);
  };

  const lockScroll = (lock) => {
    if (!chatAreaRef?.current) return;
    chatAreaRef.current.style.overscrollBehavior = lock ? "contain" : "";
    chatAreaRef.current.style.overflowY = lock ? "hidden" : "";
  };

  const handHovered = (hover) => {
    setHovered(hover);
  };

  return (
    <div className={styles.chatMessage}>
      <div
        onMouseEnter={!isTouchDevice ? () => handHovered(true) : undefined}
        onMouseLeave={!isTouchDevice ? () => handHovered(false) : undefined}
        onTouchStart={(e) =>
          handleStart(
            e.touches[0].clientX,
            e.touches[0].clientY,
            message.chatId
          )
        }
        onTouchMove={(e) => {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
          if (swipeDistance > 0) e.preventDefault();
        }}
        onTouchEnd={handleEnd}
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          handleStart(e.clientX, e.clientY, message.chatId);
        }}
        onMouseMove={(e) => {
          if (e.buttons !== 1) return;
          handleMove(e.clientX, e.clientY);
        }}
        onMouseUp={(e) => {
          if (e.button !== 0) return;
          handleEnd();
        }}
        className={styles.chatBubbleCase}
      >
        <ChatBubble
          chatAreaRef={chatAreaRef}
          currentUserId={currentUserId}
          message={message}
          swipeDistance={swipeDistance}
          showDropdown={showDropdown}
          onClickReference={onClickReference}
          bubbleRef={bubbleRef}
          dropdownRef={dropdownRef}
          isHolding={isHolding}
          hovered={hovered}
          handleOpenMedia={handleOpenMedia}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          handleCopy={handleCopy}
          handleReply={handleReply}
          handleRetry={handleRetry}
          closeMenu={closeMenu}
          showSenderName={showSenderName}
        />
      </div>
      {/* reply indicator */}
      <div
        className={styles.replyIndicator}
        style={{
          opacity: Math.min(swipeDistance / LOCK_POINT, 1),
          transform: `translate(${Math.min(
            swipeDistance,
            LOCK_POINT
          )}px, -50%)`,
        }}
      >
        {replyIcon}
      </div>
    </div>
  );
};

export default ChatMessage;
